import React, { useCallback, useEffect, useState } from 'react';
import { authApi } from '../../services/authApi';

const PerkIcon = ({ children }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const PERKS = [
  {
    icon: (
      <PerkIcon>
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
        <polyline points="16 7 22 7 22 13" />
      </PerkIcon>
    ),
    title: 'Rank higher',
    desc: 'Top of search and browse. More eyes, more fans, more growth.',
  },
  {
    icon: (
      <PerkIcon>
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
      </PerkIcon>
    ),
    title: 'Discovery spotlight',
    desc: 'Featured where new viewers land every day.',
  },
  {
    icon: (
      <PerkIcon>
        <line x1="12" x2="12" y1="2" y2="22" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </PerkIcon>
    ),
    title: '90% donation revenue',
    desc: 'Keep 90% of every spade your fans tip you.',
  },
];

const PRICE_LABEL = '$15 / month';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function PlatformPlanSection({ banner, onBannerClear }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await authApi.getPlatformPlanStatus();
      setStatus(data || null);
    } catch (err) {
      setError(err?.message || 'Failed to load Discovery Plan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  const handleSubscribe = async () => {
    setError('');
    setSubmitting(true);
    try {
      const result = await authApi.startPlatformPlanSubscribe();
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      await load();
    } catch (err) {
      setError(err?.message || 'Could not start checkout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setError('');
    setCancelling(true);
    try {
      const next = await authApi.cancelPlatformPlan();
      setStatus(next);
    } catch (err) {
      setError(err?.message || 'Failed to cancel plan');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setError('');
    setReactivating(true);
    try {
      const next = await authApi.reactivatePlatformPlan();
      setStatus(next);
    } catch (err) {
      setError(err?.message || 'Failed to reactivate plan');
    } finally {
      setReactivating(false);
    }
  };

  const isSubscribed = !!status?.is_subscribed;
  const cancelScheduled = isSubscribed && !!status?.cancel_at_period_end;
  const sectionModifier = isSubscribed
    ? 'subscriptions-page__card subscriptions-page__card--pro subscriptions-page__card--pro-active'
    : 'subscriptions-page__card subscriptions-page__card--pro';

  const renderFooter = () => {
    if (loading) {
      return <p className="subscriptions-page__muted">Loading…</p>;
    }

    if (cancelScheduled) {
      return (
        <div className="subscriptions-page__pro-cancelled">
          <div className="subscriptions-page__pro-cancelled-header">
            <span className="subscriptions-page__pro-badge subscriptions-page__pro-badge--cancelled">
              Cancelled
            </span>
            <span className="subscriptions-page__pro-cancelled-label">No further charges</span>
          </div>
          <p className="subscriptions-page__pro-cancelled-msg">
            Discovery perks remain active until
          </p>
          <p className="subscriptions-page__pro-cancelled-date">
            {formatDate(status?.current_period_end)}
          </p>
          <button
            type="button"
            className="subscriptions-page__pro-btn"
            onClick={handleReactivate}
            disabled={reactivating}
          >
            {reactivating ? 'Reactivating…' : 'Reactivate plan'}
          </button>
        </div>
      );
    }

    if (isSubscribed) {
      return (
        <div className="subscriptions-page__pro-active">
          <div className="subscriptions-page__pro-active-row">
            <span className="subscriptions-page__pro-badge">Active</span>
            <span className="subscriptions-page__pro-renewal">
              Renews {formatDate(status?.current_period_end)}
            </span>
          </div>
          <button
            type="button"
            className="subscriptions-page__secondary subscriptions-page__secondary--danger"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling…' : 'Cancel plan'}
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="subscriptions-page__pro-btn"
        onClick={handleSubscribe}
        disabled={submitting}
      >
        {submitting ? 'Redirecting to checkout…' : `Get discovered — ${PRICE_LABEL}`}
      </button>
    );
  };

  return (
    <section className={sectionModifier}>
      <div className="subscriptions-page__pro-header">
        <h2 className="subscriptions-page__pro-title">
          <span className="subscriptions-page__pro-star" aria-hidden="true">★</span>
          Get Discovered
        </h2>
        <span className="subscriptions-page__pro-label subscriptions-page__pro-label--price">
          {PRICE_LABEL}
        </span>
      </div>
      <p className="subscriptions-page__pro-subtitle">
        Grow your audience and keep 90% of every donation your viewers send.
      </p>

      {banner && (
        <div
          className={`subscriptions-page__alert subscriptions-page__alert--${banner.type}`}
          role="status"
        >
          <span>{banner.message}</span>
          {onBannerClear && (
            <button
              type="button"
              className="subscriptions-page__alert-close"
              onClick={onBannerClear}
              aria-label="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div className="subscriptions-page__pro-perks">
        <p className="subscriptions-page__pro-perks-label">What you get</p>
        <div className="subscriptions-page__pro-perks-grid">
          {PERKS.map((perk) => (
            <div key={perk.title} className="subscriptions-page__pro-perk">
              <span className="subscriptions-page__pro-perk-icon">{perk.icon}</span>
              <div>
                <p className="subscriptions-page__pro-perk-title">{perk.title}</p>
                <p className="subscriptions-page__pro-perk-desc">{perk.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="subscriptions-page__alert subscriptions-page__alert--error">{error}</div>
      )}

      {renderFooter()}
    </section>
  );
}

export default PlatformPlanSection;
